// Module Configuration - Flat structure for easy access
export const moduleConfig = {
  procurement: {
    enabled: true,
    name: 'Procurement',
    description: 'Incoming prawn lots management',
    roles: ['admin', 'owner', 'procurement_manager', 'data_entry']
  },
  purchaseBooking: {
    enabled: true,
    name: 'Purchase Booking',
    description: 'Pre-harvest booking orders that convert to purchase invoices',
    roles: ['admin', 'owner', 'procurement_manager', 'data_entry']
  },
  preprocessing: {
    enabled: true,
    name: 'Pre-Processing',
    description: 'Batch processing and yield tracking',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  unifiedWorkflow: {
    enabled: true,
    name: 'Workflow Board',
    description: 'One lot-scoped board across pre-processing and production, with click-through card details',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'qc_officer', 'data_entry']
  },
  plantOverview: {
    enabled: true,
    name: 'Plant Overview',
    description: 'Whole-plant state on one page: material pools in flow order, a pivotable cold-store stock summary, and per-order fulfilment coverage',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'sales_manager']
  },
  coldStorageWorkflow: {
    enabled: true,
    name: 'Cold Storage Band',
    description: 'Adds the cold-storage band (intake → storage → reserved → picked → dispatched) as the last row of the Workflow Board',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'sales_manager', 'data_entry']
  },
  preprocessingHome: {
    enabled: true,
    name: 'Pre-Processing Home',
    description: 'Guided launcher + at-a-glance stage board across the four pre-processing screens (re-work Phase 3)',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'data_entry']
  },
  productionAnalytics: {
    enabled: true,
    name: 'Production Analytics',
    description: 'Production traveler wizard, journey map, carton builder, yield gauge, glaze guardrail + soak/glaze reports (re-work Phase 4)',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  productionTaskRunner: {
    enabled: true,
    name: 'Production Task Runner',
    description: 'Role-aware work queue and scan-first one-screen shop-floor runner',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer', 'worker', 'data_entry']
  },
  production: {
    enabled: true,
    name: 'Production',
    description: 'Production orders and conversion',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  productionWorkflowBoard: {
    enabled: true,
    name: 'Production Workflow Board (legacy)',
    description: 'The production-only Kanban board, superseded by the unified Workflow Board',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer', 'data_entry']
  },
  productionTraveler: {
    enabled: true,
    name: 'Production Traveler',
    description: 'Stage-based traveler: Value Addition → Treatment → Freezing → Packing',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer', 'data_entry']
  },
  productionDetailEntry: {
    enabled: false,
    name: 'Production Detail Entry',
    description: 'Pick a lot, then book its output line items on one form',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  productionStageSkip: {
    enabled: false,
    name: 'Production Stage Skip',
    description: 'Skip a stage that does not apply to a lot, with a reason; weight passes through',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  lotDataCorrection: {
    enabled: false,
    name: 'Lot Data Correction',
    description: 'Correct a mis-keyed completed stage in place, with a reason, and recompute everything derived from it',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  lotWiseProduction: {
    enabled: false,
    name: 'Lot-Wise Production & Intake',
    description: 'Group the production queue and travelers by procurement lot; pending cold-storage intake queue',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge']
  },
  productionTreatmentStage: {
    enabled: true,
    name: 'Production: Treatment Stage',
    description: 'Soaking/treatment operation with recipe, soak time and gain',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer']
  },
  productionFreezingStage: {
    enabled: true,
    name: 'Production: Freezing Stage',
    description: 'Freezing operation (IQF/Block/Semi-IQF) with temp, time, glaze',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer']
  },
  productionPackingStage: {
    enabled: true,
    name: 'Production: Packing Stage',
    description: 'Packing operation: size count, carton math, emit finished goods',
    roles: ['admin', 'owner', 'production_supervisor', 'qc_officer']
  },
  productionRoutes: {
    enabled: true,
    name: 'Production Routes',
    description: 'Editable per-tenant production routes (skip treatment for untreated)',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  productionSplitMerge: {
    enabled: true,
    name: 'Production Split & Merge',
    description: 'Split a traveler into grade outputs or merge travelers',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  countGrading: {
    enabled: true,
    name: 'Count Grading',
    description: 'Per-lot count/size grading and yield chain',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  preprocessingDefects: {
    enabled: true,
    name: 'Pre-Processing Defects',
    description: 'Per-lot defect capture and dispositions',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  lotPooling: {
    enabled: true,
    name: 'Production Day Pooling',
    description: 'Pool several production days together with weighted costing',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  labourCosting: {
    enabled: true,
    name: 'Labour Costing',
    description: 'Labour entries and cost rollup',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  floorBalance: {
    enabled: true,
    name: 'Floor Balance',
    description: 'Per-window floor balance reconciliation and leakage alerts',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  glazeControl: {
    enabled: true,
    name: 'Glaze Net/Gross Control',
    description: 'Net vs gross weights and declared-vs-actual glaze variance',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  productionPooling: {
    enabled: true,
    name: 'Production Lot Proration',
    description: 'Partial batch consumption with per-lot cost and wastage proration',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  fgPacking: {
    enabled: true,
    name: 'Finished Goods Packing',
    description: 'Packing-style master and carton math on finished goods',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  honToFinished: {
    enabled: true,
    name: 'HON-to-Finished Analysis',
    description: 'Cumulative lot to finished-good yield and cost analysis',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  packingStyles: {
    enabled: true,
    name: 'Packing Styles',
    description: 'Configure finished-goods packing styles and carton math',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  defectMasters: {
    enabled: true,
    name: 'Defect Masters',
    description: 'Configure pre-processing defect types',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  sizeGrades: {
    enabled: true,
    name: 'Size Grades',
    description: 'Configure count/size grade masters',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  qc: {
    enabled: true,
    name: 'Quality Checks',
    description: 'Daily receiving and finished-product checks',
    roles: ['admin', 'owner', 'qc_officer', 'data_entry']
  },
  qcLab: {
    enabled: true,
    name: 'Lab Test Results',
    description: 'Record laboratory and instrument-test readings',
    roles: ['admin', 'owner', 'qc_officer', 'production_supervisor', 'data_entry']
  },
  qcCheckpoints: {
    enabled: true,
    name: 'Process Checkpoints',
    description: 'Optional formal check records at selected line stages',
    roles: ['admin', 'owner', 'qc_officer', 'production_supervisor']
  },
  onlineInspection: {
    enabled: true,
    name: 'Online Inspection',
    description: 'The batch inspection sheet — timed sample columns, uniformity ratio, counts, glaze and defects',
    // The floor roles who actually hold the tray. Verification and hold release
    // are gated separately and more tightly, server-side, in `_INSPECTION_VERIFY_ROLES`.
    roles: ['admin', 'owner', 'qc_officer', 'production_supervisor', 'data_entry']
  },
  uniformityDashboard: {
    enabled: true,
    name: 'Uniformity Monitor',
    description: 'Uniformity ratio trend by grader, outlet, grade and material source',
    roles: ['admin', 'owner', 'qc_officer', 'production_supervisor']
  },
  uniformityMasters: {
    enabled: true,
    name: 'Inspection Masters',
    description: 'Uniformity specifications, grading machines and the inspection sheet rows',
    roles: ['admin', 'owner', 'qc_officer', 'data_entry']
  },
  qcCompliance: {
    enabled: true,
    name: 'Shipment QC Release',
    description: 'Final buyer/country evidence check before dispatch',
    // Data entry assembles the evidence; approving the release is the Shipment
    // QC Release manager's, enforced by the server rather than hidden here.
    roles: ['admin', 'owner', 'qc_officer', 'sales_manager', 'data_entry']
  },
  qcHardGate: {
    enabled: true,
    name: 'QC Hard Gating',
    description: 'Enforce blocking at checkpoints marked blocking',
    roles: ['admin', 'owner', 'qc_officer']
  },
  qcKitsMedia: {
    enabled: true,
    name: 'QC Kits & Media',
    description: 'Reagent/media consumption via inventory',
    roles: ['admin', 'owner', 'qc_officer']
  },
  eiaDocumentation: {
    enabled: true,
    name: 'EIA Documentation',
    description: 'Pre-shipment health-certificate data + export',
    roles: ['admin', 'owner', 'qc_officer', 'sales_manager']
  },
  qcAudits: {
    enabled: true,
    name: 'Factory QC Audits',
    description: 'Scheduled hygiene, SOP, and facility audits',
    roles: ['admin', 'owner', 'qc_officer']
  },
  coldStorage: {
    enabled: true,
    name: 'Cold Storage',
    description: 'Inventory and temperature monitoring',
    roles: ['admin', 'owner', 'cold_storage_incharge', 'data_entry']
  },
  freezerUtilization: {
    enabled: false,
    name: 'Freezer Utilization',
    description: 'Per-chamber freezer occupancy % with fill gauges and ageing overlay',
    roles: ['admin', 'owner', 'cold_storage_incharge']
  },
  palletizedColdStorage: {
    enabled: true,
    name: 'Palletized Cold Storage & FEFO',
    description: 'Handling units, status buckets, FEFO order reservations and scan-based picking',
    roles: ['admin', 'owner', 'cold_storage_incharge', 'sales_manager']
  },
  lineItemColdStorage: {
    enabled: false,
    name: 'Line-Item Cold Storage',
    description: 'Store each production line item as its own cold-store balance, with Production-Order linkage',
    roles: ['admin', 'owner', 'cold_storage_incharge', 'production_supervisor']
  },
  coldStorageInsights: {
    enabled: false,
    name: 'Cold Storage Insights',
    description: 'Ageing, rework, stock value & mix, and dispatch-readiness insights for admin',
    roles: ['admin', 'owner', 'cold_storage_incharge']
  },
  finishedGoods: {
    enabled: true,
    name: 'Finished Goods',
    description: 'Ready inventory for dispatch',
    roles: ['admin', 'owner', 'production_supervisor', 'data_entry']
  },
  merchantProcessing: {
    enabled: true,
    name: 'Merchants',
    description: "Job work run on a merchant exporter's own material",
    roles: ['admin', 'owner', 'cold_storage_incharge', 'accounts_manager', 'sales_manager']
  },
  storeMovements: {
    enabled: true,
    name: 'Cold Store Outflow',
    description: 'Movement ledger and shrinkage tracking',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'data_entry']
  },
  storeView: {
    enabled: true,
    name: 'Digital Store View',
    description: 'Chamber/rack/slot occupancy map',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'data_entry']
  },
  inventoryItems: {
    enabled: true,
    name: 'Stock',
    description: 'Live stock, Sales Order holds, shortages, item ledger, packing recipe and the stock register',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'data_entry']
  },
  inventoryRequests: {
    enabled: true,
    name: 'Requests',
    description: 'Raise store receipts, issues, damage, counts and purchase needs for approval',
    // Stakeholders are configured per request type in Admin, so any role may be
    // asked to decide; the server scopes what each login sees and may do.
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge', 'data_entry', 'worker',
      'accounts_manager', 'procurement_manager', 'qc_officer', 'sales_manager'],
  },
  purchaseOrders: {
    enabled: true,
    name: 'Purchasing',
    description: 'Purchase orders and vendors — approved purchase requests become signed orders',
    // Who raises and who approves orders is configured in Admin → Inventory
    // Approvals; the server narrows each login to that. `data_entry` is here for
    // the Vendors tab, which it already kept before Purchasing absorbed it.
    roles: ['admin', 'owner', 'accounts_manager', 'procurement_manager', 'production_supervisor',
      'cold_storage_incharge', 'qc_officer', 'sales_manager', 'data_entry'],
  },
  indents: {
    enabled: true,
    name: 'Indents',
    description: 'Material indent request and issue workflow',
    roles: ['admin', 'owner', 'production_supervisor', 'cold_storage_incharge']
  },
  agents: {
    enabled: true,
    name: 'Agents & Vendors',
    description: 'Vendor management',
    roles: ['admin', 'owner', 'procurement_manager', 'data_entry']
  },
  parties: {
    enabled: true,
    name: 'Party Master',
    description: 'Farmers, suppliers, buyers and agents master',
    roles: ['admin', 'owner', 'procurement_manager', 'accounts_manager', 'data_entry']
  },
  sales: {
    enabled: true,
    name: 'Sales & Dispatch',
    description: 'Buyer management and orders',
    roles: ['admin', 'owner', 'sales_manager', 'data_entry']
  },
  clientIntelligence: {
    enabled: true,
    name: 'Client Intelligence',
    description: 'Buyer 360, churn watchlist, interactions & health',
    roles: ['admin', 'owner', 'sales_manager']
  },
  priceBenchmarking: {
    enabled: true,
    name: 'Price Benchmarking',
    description: 'Realised prices, reference prices & configurable benchmark',
    roles: ['admin', 'owner', 'sales_manager']
  },
  costAnalyser: {
    enabled: true,
    name: 'Production Day Cost Analyser',
    description: 'Per-lot cost composition: RM at realized yield, fixed components, batch drill-down',
    roles: ['admin', 'owner', 'accounts_manager']
  },
  marginCommandCenter: {
    enabled: true,
    name: 'Margin Command Center',
    description: 'Owner control for safe buy rate, expected margin, and early headless-yield action',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'qc_officer']
  },
  costComponents: {
    enabled: true,
    name: 'Cost Components Master',
    description: 'Configure fixed cost components for the cost analyser',
    roles: ['admin', 'owner', 'data_entry']
  },
  reverseCalculator: {
    enabled: true,
    name: 'Reverse RM Calculator',
    description: 'Order → kg of HON raw material to purchase',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor']
  },
  rmRequirement: {
    enabled: true,
    name: 'RM Requirement Planning',
    description: 'Net raw-material purchase need by species/count/date',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor']
  },
  lotPnl: {
    enabled: true,
    name: 'Production Day P&L',
    description: 'Lot cost-in vs realization vs wastage = margin',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor']
  },
  procurementAlerts: {
    enabled: true,
    name: 'Procurement Alerts',
    description: 'Auto-alerts: what to buy, at what count, by when',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor']
  },
  productSpecDimensions: {
    enabled: true,
    name: 'Product Spec Dimensions',
    description: 'Freezing type, treatment/soak, and carton-based quantity',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor']
  },
  specMasters: {
    enabled: true,
    name: 'Spec Masters',
    description: 'Freezing type & treatment masters',
    roles: ['admin', 'owner', 'sales_manager', 'production_supervisor', 'data_entry']
  },
  accounts: {
    enabled: true,
    name: 'Accounts',
    description: 'Wage bills and payments',
    roles: ['admin', 'owner', 'accounts_manager', 'data_entry']
  },
  wageBillAutomation: {
    enabled: true,
    name: 'Auto Wage Bills (VA/TDS)',
    description: 'Generate VA/TDS wage bills from captured labour entries',
    roles: ['admin', 'owner', 'accounts_manager']
  },
  hrEmployees: {
    enabled: true,
    name: 'Employees & Contractors',
    description: 'Employee and labour-contractor masters',
    roles: ['admin', 'owner', 'accounts_manager', 'data_entry']
  },
  hrAttendance: {
    enabled: true,
    name: 'Attendance & Shifts',
    description: 'Attendance capture and shift scheduling',
    roles: ['admin', 'owner', 'accounts_manager', 'production_supervisor']
  },
  hrBiometric: {
    enabled: true,
    name: 'Biometric Integration',
    description: 'Biometric device config and attendance sync',
    roles: ['admin', 'owner', 'accounts_manager']
  },
  hrPayroll: {
    enabled: true,
    name: 'Payroll Processing',
    description: 'Salary structures, statutory deductions, payroll runs, payslips',
    roles: ['admin', 'owner', 'accounts_manager', 'data_entry']
  },
  hrExpenses: {
    enabled: true,
    name: 'Expenses & Reimbursement',
    description: 'Expense claims, approval, reimbursement',
    roles: ['admin', 'owner', 'accounts_manager']
  },
  admin: {
    enabled: true,
    name: 'Admin Panel',
    description: 'Approvals, photos, and system management',
    roles: ['admin', 'owner']
  },
  auditTrail: {
    enabled: true,
    name: 'Audit Trail',
    description: 'Field-level change history: who changed what, before/after values, per-user activity and per-lot tracing',
    // Everyone can open it; non-admins only ever see their own activity (enforced server-side)
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'cold_storage_incharge', 'qc_officer', 'sales_manager', 'accounts_manager']
  },
  supervision: {
    enabled: true,
    name: 'Stage Supervision',
    description: "Per-stage reporting lines and the review queue for a data-entry person's work",
    // Every stage-owning role, because each of them manages a queue. Who can
    // actually read which queue is decided server-side from the stage
    // assignments — this only decides who is offered the screen at all.
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'cold_storage_incharge', 'qc_officer', 'sales_manager', 'accounts_manager']
  },
  myChangeRequests: {
    enabled: true,
    name: 'My Change Requests',
    description: "A maker's own held changes — what is waiting, what was approved, what was rejected and why",
    // Every role, deliberately. `worker` is in neither `auditTrail` nor
    // `supervision`, and a data-entry person whose save was held is exactly the
    // person who has to be able to see what happened to it. Everyone sees only
    // their own requests, which the server enforces rather than the sidebar.
    roles: '*'
  },
  masters: {
    enabled: true,
    name: 'Masters',
    description: 'Central maintenance area for operational masters',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'cold_storage_incharge', 'qc_officer', 'sales_manager', 'accounts_manager']
  },
  wastageDashboard: {
    enabled: true,
    name: 'Wastage Dashboard',
    description: 'Yield tracking and revenue loss monitoring',
    roles: ['admin', 'owner', 'production_supervisor']
  },
  yieldBenchmarks: {
    enabled: true,
    name: 'Yield Benchmarks',
    description: 'Configure wastage thresholds',
    roles: ['admin', 'owner', 'data_entry']
  },
  gradeMixCalculator: {
    enabled: true,
    name: 'Grade Mix Calculator',
    description: 'Price a purchase against the chart, or work back from an order',
    // Analytics is a manager-facing module; data-entry/worker don't get it even
    // though this one happens to be stage-tagged to procurement/preprocessing/
    // production (see MODULE_STAGES) — keeping it manager-only here is simpler
    // than carving an exception into a whole sidebar group.
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager']
  },
  purchaseVsProduction: {
    enabled: true,
    name: 'Purchase vs Production',
    description: 'The SUNB purchase-vs-production statement: purchase lines split grade-wise, against slabs packed',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager']
  },
  gradeMixChart: {
    enabled: true,
    name: 'Expected Grade Mix',
    description: 'The yield sheet: what each purchased H/ON count should grade into',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'data_entry']
  },
  yieldAccuracy: {
    enabled: true,
    name: 'Predicted Yield',
    description: 'Learned yield expectation and how accurate it has been',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  cycleTimes: {
    enabled: true,
    name: 'Cycle Times',
    description: 'How long each stage is taking and where material is ageing',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'cold_storage_incharge', 'qc_officer']
  },
  marketRates: {
    enabled: true,
    name: 'Market Rates',
    description: 'Configure pricing for revenue calculations',
    roles: ['admin', 'owner', 'data_entry']
  },
  notifications: {
    enabled: true,
    name: 'Notifications',
    description: 'System notifications',
    roles: '*'
  },
  partyLedger: {
    enabled: true,
    name: 'Party Ledger',
    description: 'Party ledger management with FY-wise accounts',
    roles: ['admin', 'owner', 'accounts_manager', 'data_entry']
  },
  dataMigration: {
    enabled: true,
    name: 'Data Migration',
    description: 'Import the manual books — parties and opening balances — at go-live',
    // Exact roles, enforced again server-side: no stand-in or data-entry login
    // may rewrite every opening balance at once.
    roles: ['admin', 'owner', 'accounts_manager']
  },
  superAdmin: {
    enabled: true,
    name: 'Super Admin',
    description: 'Platform-wide tenant and feature management',
    roles: ['super_admin']
  },
  // Phase 10 — Lot Lifecycle & Stage Views
  lotLifecycle: {
    enabled: true,
    name: 'Production Day Lifecycle',
    description: 'Lifecycle state machine for procurement lots',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager']
  },
  lotBoard: {
    enabled: true,
    name: 'Production Day Board',
    description: 'Cross-stage Kanban board of active lots',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager', 'qc_officer', 'cold_storage_incharge']
  },
  traceability: {
    enabled: true,
    name: 'Traceability',
    description: 'Forward and backward lot, carton, stock, shipment and buyer trace',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager', 'qc_officer', 'cold_storage_incharge']
  },
  moduleStageViews: {
    enabled: true,
    name: 'Module Stage Views',
    description: 'In-stage / recently-completed / pushed-forward lens on each module page',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager', 'qc_officer', 'cold_storage_incharge']
  },
  periodRollups: {
    enabled: true,
    name: 'Period Rollups',
    description: 'Monthly/quarterly summary records for instant historical reports',
    roles: ['admin', 'owner', 'production_supervisor', 'sales_manager']
  },
  operableLotBoard: {
    enabled: true,
    name: 'Operable Production Day Board',
    description: 'Drag a lot card to the next stage with a quick-confirm that commits the real stage record',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager', 'qc_officer', 'cold_storage_incharge']
  },
  lotSplitMerge: {
    enabled: true,
    name: 'Lot Split & Merge',
    description: 'Split a lot into child lots / merge lots into one downstream run, from the board',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'sales_manager', 'qc_officer', 'cold_storage_incharge']
  },
  // Phase 12 — Procurement Batches, Pre-Processing Analysis, Analysis Report
  procurementBatches: {
    enabled: true,
    name: 'Production Days',
    description: 'production days split into pond lots with tank-wise analysis',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'data_entry']
  },
  reports: {
    enabled: true,
    name: 'Reports',
    description: 'Branded operational reports (RM Arrivals, etc.) with print and PDF download',
    roles: ['data_entry', 'admin', 'owner', 'procurement_manager', 'production_supervisor', 'accounts_manager']
  },
  preprocessingAnalysis: {
    enabled: true,
    name: 'Pre-Processing Analysis',
    description: 'Per-tank-lot analysis form with live auto-calcs',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  analysisReport: {
    enabled: true,
    name: 'Analysis Report',
    description: 'Per-production-day Analysis Report (PDF/XLSX) with admin-editable columns',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  // Phase 13 — Continuous Batch Pipeline
  batchPipeline: {
    enabled: true,
    name: 'Batch Pipeline',
    description: 'Five-stage continuous batch pipeline with carry-forward weights and stage tracker',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  pipelineReconciliation: {
    enabled: true,
    name: 'Pipeline Reconciliation',
    description: 'Auto pending/shortage reconciliation with stage-wise weight-loss breakdown',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  stageReports: {
    enabled: true,
    name: 'Stage Reports',
    description: 'Per-stage downloadable reports matching the plant stage sheets + Production Statement',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  rmMaterialAvailable: {
    enabled: true,
    name: 'Plant Raw Material Entry',
    description: 'RM inventory pool from pushed invoices; create pre-processing lots with partial-pick support and a Raw Material Arrivals PDF',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager', 'data_entry']
  },
  // Phase 15 — guided 4-stage pre-processing workflow board + batch grading
  preProcessingWorkflow: {
    enabled: true,
    name: 'Pre-Processing Workflow',
    description: 'Guided Kanban board running a batch through RM Arrival → Analysis → DeHeading → Grading, with the matching report at each stage',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  batchGrading: {
    enabled: true,
    name: 'Lot Grading',
    description: 'Per-lot grading with grade-master dropdown, count-band validation, and a downloadable Grading Report',
    roles: ['admin', 'owner', 'production_supervisor', 'procurement_manager']
  },
  vehicleCompliance: {
    enabled: true,
    name: 'Vehicle Compliance',
    description: 'Company vehicle document expiry tracking — insurance, fitness, permit, AIP, pollution with alert notifications',
    roles: ['admin', 'owner', 'procurement_manager', 'data_entry']
  },
  aiChatBot: {
    enabled: true,
    name: 'Aqua ERP AI assistance (chat)',
    description: 'Floating assistant on every screen — plain-language questions in English, Hindi or Telugu about stock, ageing, dispatch readiness, production, quality, orders and payments, plus step-by-step guidance on where things live in the portal and an honest hand-off to support when it cannot help',
    roles: ['admin', 'owner', 'procurement_manager', 'production_supervisor', 'cold_storage_incharge', 'qc_officer', 'sales_manager', 'accounts_manager']
  },
  docs: {
    enabled: true,
    name: 'Docs & Guided Help',
    description: 'Searchable process handbook with ordered click steps and optional GIF/video walkthroughs',
    // The handbook is available to every signed-in role. Opening a screen from
    // a guide still runs through the normal destination-level RBAC check.
    roles: '*'
  }
};

// ── Which stage each module belongs to ────────────────────────────────────────
// A data-entry person is hired for *one part of the plant*. The same person may
// key procurement in the morning and cold store at night, which is why the
// assignment is a list on the user (`stage_assignments`) rather than a second
// role — but a procurement data-entry person has no business opening the
// production traveler, and showing them a screen they will be refused at is
// worse than not showing it.
//
// Stage keys are the supervision engine's, which are in turn `audit_registry`'s
// module codes. One vocabulary: the stage that decides whose queue an edit lands
// in is the same stage that decides whether the screen is in the sidebar, so the
// two can never drift into disagreeing about what "procurement" means.
//
// A module listed with several stages is genuinely shared — the Workflow Board
// spans pre-processing and production, the party master is keyed by procurement
// and read by accounts — and is shown to anybody assigned to any one of them.
//
// This map must agree with `ACCESS_ROUTE_STAGES` in
// backend/services/supervision_registry.py. They are two halves of one rule, and
// a disagreement shows up as a screen that is offered and then refuses to load,
// which reads to an operator as a broken page rather than as a permission they
// do not have. A module absent from this map is not stage-scoped: it is either a
// manager's screen (kept out of role 1 by `roles`) or something everybody gets.
export const MODULE_STAGES = {
  procurement: ['procurement'],
  purchaseBooking: ['procurement'],
  parties: ['procurement', 'accounts'],
  rmMaterialAvailable: ['procurement', 'preprocessing'],
  vehicleCompliance: ['procurement'],
  agents: ['procurement'],
  ponds: ['procurement'],

  procurementBatches: ['preprocessing'],
  preprocessing: ['preprocessing'],
  preprocessingHome: ['preprocessing'],
  preprocessingDefects: ['preprocessing'],
  countGrading: ['preprocessing'],
  lotPooling: ['preprocessing'],

  unifiedWorkflow: ['preprocessing', 'production'],

  production: ['production'],
  productionAnalytics: ['production'],
  productionTaskRunner: ['production'],
  productionWorkflowBoard: ['production'],
  productionTraveler: ['production'],
  productionDetailEntry: ['production'],
  gradeMixChart: ['preprocessing', 'production'],
  // Answered before anything is bought, so procurement is its home stage;
  // production reads it too when planning what a batch will give.
  gradeMixCalculator: ['procurement', 'preprocessing', 'production'],
  purchaseVsProduction: ['procurement', 'preprocessing', 'production'],

  onlineInspection: ['quality'],
  qc: ['quality'],
  qcLab: ['quality'],
  qcCheckpoints: ['quality'],
  qcAudits: ['quality'],
  uniformityDashboard: ['quality'],
  uniformityMasters: ['quality'],

  // Shipment QC Release — its own stage, so the person who signs a container out
  // need not be the one running floor inspections.
  qcCompliance: ['shipment_release'],
  eiaDocumentation: ['shipment_release'],

  coldStorage: ['coldstorage'],
  coldStorageWorkflow: ['coldstorage'],
  storeMovements: ['coldstorage'],
  storeView: ['coldstorage'],
  finishedGoods: ['production', 'coldstorage'],

  sales: ['sales'],
  accounts: ['accounts'],
  partyLedger: ['accounts'],
  dataMigration: ['accounts'],

  inventoryItems: ['inventory'],
  inventoryRequests: ['inventory'],
  purchaseOrders: ['inventory'],
  indents: ['inventory'],
  hrEmployees: ['hr'],
  hrPayroll: ['hr'],
};

// Roles whose sidebar is narrowed to the stages they are assigned to: the entry
// tier and `stage_manager`, who leads whichever stages are on their profile. The
// fixed stage roles (procurement_manager, …) are not — their reach is their role.
// Server: supervision_registry.STAGE_SCOPED_ROLES.
export const STAGE_SCOPED_ROLES = ['data_entry', 'worker', 'stage_manager'];

// The stage roles a stage-scoped login stands in for, and the stages each one
// owns. Mirrors ROLE_STANDS_IN_FOR / ROLE_STAGE_SCOPE in server.py: a stand-in
// counts only for a stage the person actually holds.
export const ROLE_STAGE_SCOPE = {
  procurement_manager: ['procurement'],
  production_supervisor: ['preprocessing', 'production'],
  cold_storage_incharge: ['coldstorage', 'inventory'],
  qc_officer: ['quality', 'shipment_release'],
};

// `worker` is the same tier as `data_entry` — same screens, same stage scope, same
// approval rules (server: supervision_registry.ENTRY_ROLES). Role lists below name
// `data_entry` only; a worker is matched through this alias rather than being
// added to every list by hand and missing some. A stage manager reaches every
// screen data entry does, plus the manager screens of the stage roles it stands
// in for — narrowed per person by `moduleInUserStages`.
export const ROLE_ALIASES = {
  worker: ['data_entry'],
  stage_manager: ['data_entry', ...Object.keys(ROLE_STAGE_SCOPE)],
};

const roleListIncludes = (roles, userRole) =>
  roles.includes(userRole) || (ROLE_ALIASES[userRole] || []).some((alias) => roles.includes(alias));

/** The stages a login is assigned to, as a plain array of keys. */
export function assignedStages(user) {
  const raw = Array.isArray(user?.stage_assignments) ? user.stage_assignments : [];
  return raw.map((a) => a && a.stage).filter(Boolean);
}

/**
 * Whether this login's stage assignments reach this module.
 *
 * Returns true for every role that is not stage-scoped, and for every module
 * that is not stage-tagged, so this is a *narrowing* gate only — it can never
 * grant a module the role checks did not already allow. That direction matters:
 * a widening gate here would quietly become a second, weaker authorisation path.
 */
export function moduleInUserStages(moduleKey, user) {
  const role = user?.role;
  if (!STAGE_SCOPED_ROLES.includes(role)) return true;
  const mine = assignedStages(user);
  const stages = MODULE_STAGES[moduleKey];
  if (stages && stages.length > 0) return stages.some((stage) => mine.includes(stage));

  // Untagged module: not stage-tagged, so it is either a manager's screen or a
  // genuinely shared surface. The server's role-feature matrix grants `role`
  // every code *any* role it stands in for can reach (`ROLE_STANDS_IN_FOR`) —
  // that stand-in is deliberately an outer bound the sidebar is supposed to
  // narrow back down by stage. For an untagged module there is no stage to
  // narrow by, so without a check here the stand-in grant reaches the sidebar
  // whole: a procurement data-entry person would get every analytics/HR/sales
  // screen any stage manager anywhere can open, not just procurement's.
  if (role === 'data_entry' || role === 'worker') {
    // Role 1 only ever gets what `roles` names for them directly — never a
    // manager's stand-in reach into an unscoped screen. (A *stage manager*,
    // below, is a real manager and is narrowed by stage instead of cut off
    // outright — see the stand-in check further down.)
    const roles = moduleConfig[moduleKey]?.roles;
    return !!roles && (roles === '*' || roles.includes('data_entry') || roles.includes('worker'));
  }

  if (role !== 'stage_manager') return true;
  // An untagged manager screen (a dashboard, a masters page) is reached by a
  // stage manager only through a stand-in role — and only one whose stage they
  // hold, exactly as `_stands_in_for` decides on the server. Without this a
  // procurement lead would be offered every QC manager screen.
  const roles = moduleConfig[moduleKey]?.roles;
  if (!roles || roles === '*' || roles.includes('data_entry') || roles.includes(role)) return true;
  return Object.entries(ROLE_STAGE_SCOPE).some(
    ([standIn, owned]) => roles.includes(standIn) && owned.some((stage) => mine.includes(stage)),
  );
}

// Dashboard access control
export const dashboardAccessRoles = [
  'admin',
  'owner',
  'procurement_manager',
  'production_supervisor',
  'cold_storage_incharge',
  'qc_officer',
  'sales_manager',
];

export const canAccessDashboard = (userRole) => {
  return roleListIncludes(dashboardAccessRoles, userRole);
};

export const isModuleAccessible = (moduleKey, userRole) => {
  const module = moduleConfig[moduleKey];
  if (!module || !module.enabled) return false;
  if (module.roles === '*') return true;
  return roleListIncludes(module.roles, userRole);
};

// Ordered list of pages that can be granted/revoked per user.
// key matches moduleKey in the Layout.js navigation array.
export const ALL_PAGES = [
  { key: 'purchaseBooking', name: 'Purchase Booking', group: 'Procurement' },
  { key: 'agents', name: 'Agents & Vendors', group: 'Procurement' },
  { key: 'parties', name: 'Party Master', group: 'Procurement' },
  { key: 'vehicleCompliance', name: 'Vehicle Compliance', group: 'Procurement' },
  // Overview, Workflow Board and Production are tabs of one page now, but each is
  // still grantable on its own — the tabs honour page_permissions the way the nav
  // entries they replaced did.
  { key: 'procurement', name: 'Overview', group: 'Pre-Processing' },
  { key: 'unifiedWorkflow', name: 'Workflow Board', group: 'Production' },
  { key: 'rmMaterialAvailable', name: 'Plant Raw Material Entry', group: 'Pre-Processing' },
  { key: 'preprocessingHome', name: 'Pre-Processing Home', group: 'Pre-Processing' },
  { key: 'preprocessing', name: 'Pre-Processing Entry', group: 'Pre-Processing' },
  { key: 'procurementBatches', name: 'Production Days', group: 'Pre-Processing' },
  { key: 'analysisReport', name: 'Analysis Report Template', group: 'Masters' },
  { key: 'preProcessingWorkflow', name: 'Pre-Processing Workflow', group: 'Pre-Processing' },
  { key: 'batchPipeline', name: 'Batch Pipeline', group: 'Production' },
  { key: 'defectMasters', name: 'Defect Masters', group: 'Masters' },
  { key: 'sizeGrades', name: 'Size Grades', group: 'Masters' },
  { key: 'floorBalance', name: 'Floor Balance', group: 'Pre-Processing' },
  { key: 'production', name: 'Production / Production Entry', group: 'Production' },
  { key: 'packingStyles', name: 'Packing Styles', group: 'Masters' },
  { key: 'qc', name: 'Quality Checks', group: 'Quality Checks' },
  { key: 'qcLab', name: 'Lab Test Results', group: 'Quality Checks' },
  { key: 'qcCheckpoints', name: 'Process Checkpoints', group: 'Quality Checks' },
  { key: 'onlineInspection', name: 'Online Inspection', group: 'Quality Checks' },
  { key: 'uniformityDashboard', name: 'Uniformity Monitor', group: 'Quality Checks' },
  { key: 'uniformityMasters', name: 'Inspection Masters', group: 'Quality Checks' },
  { key: 'qcCheckpoints', name: 'QC Masters', group: 'Masters' },
  { key: 'qcCompliance', name: 'Shipment QC Release', group: 'Sales' },
  { key: 'qcAudits', name: 'Factory QC Audits', group: 'Admin' },
  { key: 'auditTrail', name: 'Audit Trail', group: 'Admin' },
  { key: 'eiaDocumentation', name: 'EIA Certificate', group: 'Sales' },
  { key: 'coldStorage', name: 'Cold Storage', group: 'Storage' },
  { key: 'storeView', name: 'Store View', group: 'Storage' },
  { key: 'storeMovements', name: 'Cold Store Outflow', group: 'Storage' },
  { key: 'finishedGoods', name: 'Finished Goods', group: 'Storage' },
  { key: 'inventoryItems', name: 'Stock', group: 'Inventory' },
  { key: 'inventoryRequests', name: 'Requests', group: 'Inventory' },
  { key: 'purchaseOrders', name: 'Purchasing (orders & vendors)', group: 'Inventory' },
  { key: 'indents', name: 'Indents (legacy)', group: 'Inventory' },
  { key: 'sales', name: 'Sales & Dispatch', group: 'Sales' },
  { key: 'clientIntelligence', name: 'Client Intelligence', group: 'Sales' },
  { key: 'priceBenchmarking', name: 'Price Benchmarking', group: 'Analytics' },
  { key: 'accounts', name: 'Accounts', group: 'Finance' },
  { key: 'partyLedger', name: 'Party Ledger', group: 'Finance' },
  { key: 'dataMigration', name: 'Data Migration', group: 'Finance' },
  { key: 'hrEmployees', name: 'Employees', group: 'HR' },
  { key: 'hrAttendance', name: 'Attendance', group: 'HR' },
  { key: 'hrPayroll', name: 'Payroll', group: 'HR' },
  { key: 'hrExpenses', name: 'Expenses', group: 'HR' },
  { key: 'reverseCalculator', name: 'Reverse RM Calculator', group: 'Analytics' },
  { key: 'specMasters', name: 'Spec Masters', group: 'Masters' },
  { key: 'rmRequirement', name: 'RM Requirement Planning', group: 'Analytics' },
  { key: 'lotPnl', name: 'Production Day P&L', group: 'Analytics' },
  { key: 'costAnalyser', name: 'Cost Analyser', group: 'Analytics' },
  { key: 'costComponents', name: 'Cost Components Master', group: 'Masters' },
  { key: 'procurementAlerts', name: 'Procurement Alerts', group: 'Sales' },
  { key: 'lotBoard', name: 'Production Day Board', group: 'Analytics' },
  { key: 'traceability', name: 'Traceability', group: 'Analytics' },
  { key: 'wastageDashboard', name: 'Wastage Dashboard', group: 'Analytics' },
  { key: 'yieldBenchmarks', name: 'Yield Benchmarks', group: 'Masters' },
  { key: 'yieldAccuracy', name: 'Predicted Yield', group: 'Analytics' },
  { key: 'cycleTimes', name: 'Cycle Times', group: 'Analytics' },
  { key: 'marketRates', name: 'Market Rates', group: 'Masters' },
  { key: 'reports', name: 'Reports', group: 'Reports' },
  { key: 'masters', name: 'Masters', group: 'Masters' },
  { key: 'admin', name: 'Admin Panel', group: 'Admin' },
  { key: 'docs', name: 'Docs & Guided Help', group: 'Help' },
];
